// src/components/NotificationPrompt.js
import React, { useState, useEffect } from 'react';
import usePushNotifications from '../hooks/usePushNotifications';

/**
 * NotificationPrompt Component
 * Shows a dismissible prompt to enable push notifications
 * 
 * Usage:
 * <NotificationPrompt />
 * 
 * Optional props:
 * - autoShow: automatically show if notifications are not enabled (default: true)
 * - onDismiss: callback when prompt is dismissed
 * - onEnable: callback when notifications are enabled
 * - storageKey: localStorage key for dismissed state (default: 'notificationPromptDismissed')
 * - showDelay: delay in ms before showing prompt (default: 2000)
 */
const NotificationPrompt = ({
  autoShow = true,
  onDismiss,
  onEnable,
  storageKey = 'notificationPromptDismissed',
  showDelay = 2000
}) => {
  const [visible, setVisible] = useState(false);
  const [enabling, setEnabling] = useState(false);
  
  const {
    isSupported,
    isEnabled,
    permission,
    enable,
    loading
  } = usePushNotifications();

  // Check if prompt should be shown
  useEffect(() => {
    if (!autoShow) return;

    // Don't show if still loading
    if (loading) return;

    // Don't show if not supported
    if (!isSupported) return;

    // Don't show if already enabled
    if (isEnabled) return;

    // Don't show if permission was denied
    if (permission === 'denied') return;

    // Don't show if user previously dismissed
    const dismissed = localStorage.getItem(storageKey);
    if (dismissed === 'true') return;

    // Show prompt after delay
    const timer = setTimeout(() => {
      setVisible(true);
    }, showDelay);

    return () => clearTimeout(timer);
  }, [autoShow, loading, isSupported, isEnabled, permission, storageKey, showDelay]);

  /**
   * Handle enable button click
   */
  const handleEnable = async () => {
    try {
      setEnabling(true);
      const result = await enable();

      if (result.success) {
        setVisible(false);
        if (onEnable) {
          onEnable();
        }
      }
    } catch (error) {
      console.error('Error enabling notifications:', error);
    } finally {
      setEnabling(false);
    }
  };

  /**
   * Handle dismiss button click
   */
  const handleDismiss = () => {
    setVisible(false);
    localStorage.setItem(storageKey, 'true');
    
    if (onDismiss) {
      onDismiss();
    }
  };

  /**
   * Handle "Not Now" button click
   */
  const handleNotNow = () => {
    setVisible(false);
    // Don't save to localStorage so it can show again later
    
    if (onDismiss) {
      onDismiss();
    }
  };

  // Don't render if not visible
  if (!visible) {
    return null;
  }

  return (
    <div
      className="notification-prompt"
      style={{
        position: 'fixed',
        bottom: '20px',
        right: '20px',
        maxWidth: '400px',
        zIndex: 9999,
        animation: 'slideInUp 0.3s ease-out'
      }}
    >
      <div className="card shadow-lg border-0">
        <div className="card-body">
          {/* Close button */}
          <button
            type="button"
            className="btn-close position-absolute top-0 end-0 m-2"
            onClick={handleDismiss}
            aria-label="Close"
            style={{ zIndex: 1 }}
          ></button>

          {/* Icon and title */}
          <div className="d-flex align-items-start mb-3">
            <div
              className="rounded-circle d-flex align-items-center justify-content-center me-3"
              style={{
                width: '48px',
                height: '48px',
                backgroundColor: '#ff6800',
                flexShrink: 0
              }}
            >
              <i className="bi bi-bell-fill text-white" style={{ fontSize: '24px' }}></i>
            </div>
            <div>
              <h5 className="mb-1">Stay Updated!</h5>
              <p className="text-muted small mb-0">
                Enable notifications to get instant updates about:
              </p>
            </div>
          </div>

          {/* Features list */}
          <ul className="small mb-3 ps-4" style={{ listStyle: 'none' }}>
            <li className="mb-1">
              <i className="bi bi-check-circle-fill me-2" style={{ color: '#ff6800' }}></i>
              Task mentions and assignments
            </li>
            <li className="mb-1">
              <i className="bi bi-check-circle-fill me-2" style={{ color: '#ff6800' }}></i>
              Event updates and reminders
            </li>
            <li className="mb-1">
              <i className="bi bi-check-circle-fill me-2" style={{ color: '#ff6800' }}></i>
              Important announcements
            </li>
          </ul>

          {/* Action buttons */}
          <div className="d-flex gap-2">
            <button
              className="btn btn-sm flex-grow-1"
              style={{
                backgroundColor: '#ff6800',
                color: 'white',
                border: 'none'
              }}
              onClick={handleEnable}
              disabled={enabling}
            >
              {enabling ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  Enabling...
                </>
              ) : (
                <>
                  <i className="bi bi-bell-fill me-2"></i>
                  Enable Notifications
                </>
              )}
            </button>
            <button
              className="btn btn-sm btn-outline-secondary"
              onClick={handleNotNow}
              disabled={enabling}
            >
              Not Now
            </button>
          </div>

          {/* Privacy note */}
          <p className="text-muted small mb-0 mt-2">
            <i className="bi bi-shield-check me-1"></i>
            You can change this anytime in settings
          </p>
        </div>
      </div>

      {/* Animation styles */}
      <style jsx>{`
        @keyframes slideInUp {
          from {
            transform: translateY(100px);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
      `}</style>
    </div>
  );
};

export default NotificationPrompt;
